import React from "react";
import type { Control, UseFormSetValue, FieldErrors } from "react-hook-form";
import { FormField } from "../../../../shared/components/forms/FormField";
import type { AdverseEventNewFormData } from "../RegisterNewForm/RegisterNewForm.types";
import { useAdverseEventFactorsFormFields } from "./useAdverseEventFactorsFormFields";

interface AdverseEventFactorsFormFieldsProps {
  control: Control<AdverseEventNewFormData>;
  setValue: UseFormSetValue<AdverseEventNewFormData>;
  errors: FieldErrors<AdverseEventNewFormData>;
  /** Numeric adverse event type ID — drives the Q3 options fetch. */
  adverseEventsTypeId: number | null;
  /** Used to personalize the Q1 label with "accidente" or "incidente". */
  isAccidentSelected?: boolean;
}

/**
 * Renders the three-question cascade for Accidente and Incidentes adverse events:
 *
 * Q1: Factor principal — always visible once mounted.
 * Q2: Factor específico — visible after Q1 answered, filtered by main factor.
 * Q3: Nivel de intervención — visible after Q1 answered, filtered by adverse event type.
 *
 * Reset side-effects are handled inside `useAdverseEventFactorsFormFields`.
 */
export const AdverseEventFactorsFormFields: React.FC<
  AdverseEventFactorsFormFieldsProps
> = ({ control, setValue, errors, adverseEventsTypeId, isAccidentSelected }) => {
  const {
    mainFactorOptions,
    specificFactorOptions,
    interventionLevelOptions,
    isQ1Answered,
    isLoadingSpecificFactors,
    isLoadingInterventionLevels,
  } = useAdverseEventFactorsFormFields({ control, setValue, adverseEventsTypeId });

  return (
    <div className="flex flex-col gap-5 mt-4">
      {/* Q1 — Factor principal */}
      <FormField
        type="radioGroup"
        label={`El ${isAccidentSelected ? "accidente" : "incidente"} está relacionado con:`}
        labelClassName="text-red-600"
        required
        radioGroupProps={{
          control,
          name: "event_main_factor_id",
          options: mainFactorOptions,
        }}
        error={errors.event_main_factor_id?.message}
      />

      {/* Q2 + Q3 — appear after Q1 is answered */}
      {isQ1Answered && (
        <>
          {/* Q2 — Factor específico */}
          <FormField
            type="radioGroup"
            label="¿Qué situación describe lo ocurrido?"
            labelClassName="text-red-600"
            required
            radioGroupProps={{
              control,
              name: "event_specific_factor_id",
              options: isLoadingSpecificFactors ? [] : specificFactorOptions,
            }}
            error={errors.event_specific_factor_id?.message}
          />

          {/* Q3 — Nivel de intervención */}
          <FormField
            type="radioGroup"
            label="Nivel de intervención"
            labelClassName="text-red-600"
            required
            radioGroupProps={{
              control,
              name: "adverse_event_intervention_level_id",
              options: isLoadingInterventionLevels ? [] : interventionLevelOptions,
            }}
            error={errors.adverse_event_intervention_level_id?.message}
          />
        </>
      )}
    </div>
  );
};
